import { useMemo } from 'react';
import { useIsGuest } from './useIsGuest';
import { ROUTES, buildRoute } from '@/routes';

export interface SessionRoutes {
  hub: string;
  interview: (assessmentId: string) => string;
  results: (assessmentId: string) => string;
}

/**
 * Sessions.tsx/SessionInterview.tsx/SessionResults.tsx are shared verbatim
 * between authenticated candidates and code-redeemed guests (see
 * useSessionToken) — only the URLs they live under differ, so this is the
 * one place that picks the right route family instead of each page
 * hardcoding ROUTES.SESSIONS/SESSION_INTERVIEW/SESSION_RESULTS.
 */
export const useSessionRoutes = (): SessionRoutes => {
  const isGuest = useIsGuest();

  // Stable across renders unless isGuest itself flips, so this is safe to
  // drop straight into a useCallback/useEffect dependency array like any
  // other route constant, rather than destabilizing every consumer.
  return useMemo(
    () =>
      isGuest
        ? {
            hub: ROUTES.GUEST_SESSIONS,
            interview: (assessmentId: string) =>
              buildRoute(ROUTES.GUEST_SESSION_INTERVIEW, { assessmentId }),
            results: (assessmentId: string) =>
              buildRoute(ROUTES.GUEST_SESSION_RESULTS, { assessmentId }),
          }
        : {
            hub: ROUTES.SESSIONS,
            interview: (assessmentId: string) =>
              buildRoute(ROUTES.SESSION_INTERVIEW, { assessmentId }),
            results: (assessmentId: string) =>
              buildRoute(ROUTES.SESSION_RESULTS, { assessmentId }),
          },
    [isGuest]
  );
};

export default useSessionRoutes;
